// Older javascript code should continue to import this module like this:
//
// var sutl = require('../../sutl/Sutl');
// sutl.setProp(...);
//
// Newer typescript code should import this module like this:
//
// import { Sutl } from '../../sutl;
// Sutl.setProp(...);
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Convert an array to an object.
export function arrayToObj(arr, prefix = '') {
    const obj = {};
    let index = 0;
    for (const item of arr) {
        obj[prefix + (index++).toString()] = item;
    }
    return obj;
}
// Create a deep copy of supplied argument.
export function deepCopy(orig) {
    if (orig === null)
        return orig;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (orig instanceof Date)
        return new Date(orig.getTime());
    if (orig instanceof Array) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const copy = [];
        for (const value of orig) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            copy.push(deepCopy(value));
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return copy;
    }
    if (typeof orig === 'object') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const copy = {};
        for (const key of Object.keys(orig)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            copy[key] = deepCopy(orig[key]);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return copy;
    }
    return orig;
}
// Delete the nested property on the specified object. See setNode for how to specify the path.
//
// Note: This method is deprecated for new Typescript code. In Typescript, this can be accomplised
// by using optional chaining with delete.
//
// delete foo?.[bar]?.baz;
export function deleteNode(obj, path) {
    if (typeof path === 'string')
        path = path.split('.');
    for (let i = 0; i < path.length; i++) {
        if (path[i] in obj) {
            if (i === path.length - 1) {
                delete obj[path[i]];
            }
            else {
                obj = obj[path[i]];
            }
        }
        else {
            return;
        }
    }
}
// Escape string for use in regular expression.
//
// Reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
export function escapeRegExp(str) {
    return str.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&');
}
// Filter an array in place. (Array.filter creates a new array).
//
// Reference: https://stackoverflow.com/a/37319954/122231
export function filterInPlace(arr, test) {
    let i = 0;
    let j = 0;
    while (i < arr.length) {
        const val = arr[i];
        if (test(val, i))
            arr[j++] = val;
        i++;
    }
    arr.length = j;
    return arr;
}
export function forEachAsyncParallel(items, fn) {
    return __awaiter(this, void 0, void 0, function* () {
        const promises = [];
        items.forEach((item, index) => promises.push(fn(item, index)));
        return Promise.all(promises);
    });
}
export function forEachAsyncSerial(items, fn) {
    return __awaiter(this, void 0, void 0, function* () {
        const result = [];
        yield items.reduce((acc, curr, index) => __awaiter(this, void 0, void 0, function* () {
            yield acc;
            result.push(yield fn(curr, index));
        }), Promise.resolve());
        return result;
    });
}
// Similar-looking characters removed: 0, o, 1, I
const alphanumericChars = '23456789abcdefghjklmnpqrstuvwxyz';
export function generateAlphanumericCode(length) {
    return generateRandomString(length, alphanumericChars);
}
// Generate a Firebase real-time database ID.
//
// References:
//
// - https://www.firebase.com/blog/2015-02-11-firebase-unique-identifiers.html
// - https://gist.github.com/mikelehen/3596a30bd69384624c11
//
// The ID characters are modeled after base64 web-safe characters, but ordered by ASCII.
//
// The firebaseIdLastPushTime value is the timestamp of the last push. It is used to prevent
// collisions if more than one ID is generated in the same millisecond.
//
// The algorithm generates 72 bits of randomness which get turned into 12 characters, then it
// appends the timestamp to prevent collisions with other clients and make the IDs sortable by
// time. The lastRandChars value stores the last characters generated by the algorithm because in
// the event of a collision, the new ID can be the previous ID incremented by one.
const firebaseIdChars = '-0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz';
let firebaseIdLastPushTime = 0;
const lastRandChars = [];
export function generateFirebaseId() {
    let now = new Date().getTime();
    const duplicateTime = now === firebaseIdLastPushTime;
    let i;
    firebaseIdLastPushTime = now;
    const timeStampChars = new Array(8);
    for (i = 7; i >= 0; i--) {
        timeStampChars[i] = firebaseIdChars.charAt(now % 64);
        // NOTE: Can't use << here because javascript will convert to int
        // and lose the upper bits.
        now = Math.floor(now / 64);
    }
    if (now !== 0) {
        throw new Error('Expected timestamp to be converted.');
    }
    let id = timeStampChars.join('');
    if (!duplicateTime) {
        for (i = 0; i < 12; i++) {
            lastRandChars[i] = Math.floor(Math.random() * 64);
        }
    }
    else {
        // If the timestamp hasn't changed since last push, use the same
        // random number, except incremented by 1.
        for (i = 11; i >= 0 && lastRandChars[i] === 63; i--) {
            lastRandChars[i] = 0;
        }
        lastRandChars[i]++;
    }
    for (i = 0; i < 12; i++) {
        id += firebaseIdChars.charAt(lastRandChars[i]);
    }
    if (id.length !== 20)
        throw new Error('Length should be 20.');
    return id;
}
export function generateRandomString(length, charSet) {
    const chars = [];
    for (let i = 0; i < length; i++) {
        const index = Math.floor(Math.random() * charSet.length);
        chars.push(charSet[index]);
    }
    return chars.join('');
}
// Get the value at the nested property of the specified object. Returns null if the property
// doesn't exist. See setNode for how to specify the path.
//
// Note: This method is deprecated for new Typescript code. In typescript, this can be accomplised
// by using optional chaning, and a default can be applied with nullish coalescing.
//
// const x = foo?.[bar]?.baz ?? 'default';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getNode(obj, path, defaultValue) {
    if (defaultValue === undefined)
        defaultValue = null;
    if (obj === undefined || obj === null)
        return defaultValue;
    if (typeof path === 'string')
        path = path.split('.');
    for (const prop of path) {
        if (obj === undefined ||
            obj === null ||
            (typeof obj !== 'object' && typeof obj !== 'function') ||
            !(prop in obj)) {
            return defaultValue;
        }
        obj = obj[prop];
        if (obj === null)
            break;
    }
    return obj;
}
// Get the value at the nested property of the specified object. Returns undefined if the property
// doesn't exist. See setProp for how to specify the path.
//
// Note: This method should be used sparingly in new Typescript code. In Typescript, this can be
// accomplished by using optional chaning, and a default can be applied with nullish coalescing.
//
// const x = foo?.[bar]?.baz ?? 'default';
//
// However, this method can still be useful when programmatically specifying complex paths.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getProp(obj, path, defaultValue) {
    if (obj === undefined || obj === null)
        return defaultValue;
    if (typeof path === 'string')
        path = path.split('.');
    for (const prop of path) {
        if (obj === undefined ||
            obj === null ||
            (typeof obj !== 'object' && typeof obj !== 'function') ||
            !(prop in obj)) {
            return defaultValue;
        }
        obj = obj[prop];
        if (obj === undefined || obj === null)
            return undefined;
    }
    return obj;
}
// Perform a merge of object b into object a (object a is modified).
export function merge(a, b) {
    if (a === undefined || a === null || typeof a !== 'object')
        return b;
    if (b === undefined || b === null || typeof b !== 'object')
        return b;
    const aa = a;
    const bb = b;
    for (const prop of Object.keys(bb)) {
        if (typeof aa[prop] === 'object') {
            if (typeof bb[prop] === 'object') {
                merge(aa[prop], bb[prop]);
            }
            else {
                aa[prop] = bb[prop];
            }
        }
        else {
            aa[prop] = bb[prop];
        }
    }
    return aa;
}
// Return true if the object has a value at the specified node, otherwise return false. See setNode
// for how to specify the path.
//
// Note: This method is deprecated for new Typescript code. In Typescript, this can be accomplished
// by using optional chaining and testing the result.
//
// const exists = foo?.[bar]?.baz !== undefined;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function nodeExists(obj, path) {
    return getNode(obj, path) === null ? false : true;
}
// Return true if the two objects are equal, false if not.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function objEquals(a, b) {
    if (a === undefined && b === undefined)
        return true;
    if (a === null && b === null)
        return true;
    if (a === undefined || b === undefined || a === null || b === null)
        return false;
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length)
            return false;
        for (let i = 0; i < a.length; i++) {
            if (!objEquals(a[i], b[i]))
                return false;
        }
        return true;
    }
    if (typeof a !== 'object' || typeof b !== 'object')
        return a === b;
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length)
        return false;
    for (const aKey in a) {
        if (!(aKey in a))
            continue;
        if (!(aKey in b))
            return false;
        if (typeof a[aKey] !== typeof b[aKey])
            return false;
        if (typeof a[aKey] === 'object') {
            if (!objEquals(a[aKey], b[aKey]))
                return false;
        }
        else {
            if (a[aKey] !== b[aKey])
                return false;
        }
    }
    return true;
}
export function objToArray(obj, prefix = '') {
    let props = Object.keys(obj);
    if (prefix !== '') {
        const prefixRegexp = new RegExp(`^${prefix}`);
        props = props.map(p => p.replace(prefixRegexp, ''));
    }
    return props.map(p => parseInt(p, 10)).sort().map(n => obj[`${prefix}${n}`]);
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function pruneEmptyObjects(obj) {
    if (typeof obj !== 'object' || obj === null)
        return;
    for (const key of Object.keys(obj)) {
        const item = obj[key];
        if (typeof item !== 'object' || obj === null)
            continue;
        const keys = Object.keys(item);
        if (keys.length === 0) {
            delete obj[key];
        }
        else {
            pruneEmptyObjects(obj[key]);
        }
    }
}
// These are the characters that aren't permitted in Firebase keys.
const firebaseIdInvalidCharacters = ['.', '$', '#', '[', ']', '/'];
export function sanitizeId(id) {
    let sanitizedId = '';
    for (let i = 0; i < id.length; i++) {
        const ch = id[i];
        const code = id.charCodeAt(i);
        if (ch === '\\') {
            sanitizedId += '\\\\';
        }
        else if (code < 32 || code > 126 || firebaseIdInvalidCharacters.includes(ch)) {
            sanitizedId += `\\u${code.toString(16).padStart(4, '0')}`;
        }
        else {
            sanitizedId += ch;
        }
    }
    if (sanitizedId.length > 768)
        throw new Error('id too long');
    return sanitizedId;
}
// Set value at a nested property in an object. Create parent nodes if they don't exist. Nested
// properties are specified with the path argument in two ways.
//
// 1. A string with the properties separated by dots, e.g., `foo.${bar}.baz`.
// 2. An array, e.g., ['foo', bar, 'baz].
//
// The old setNode method is deprecated in favor of setProp which performs the same function, but
// uses a more descriptive name and supports optional generic typing.
//
// Note: unlike the other nested property utility methods, setProp is NOT deprecated for new
// Typescript code because Typescript's optional chaining does not support optional property
// assignment. (https://github.com/tc39/proposal-optional-chaining). Still, use of setProp is
// discouraged because it defeats type checking.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function setProp(obj, path, value) {
    if (obj === undefined || obj === null)
        return obj;
    if (typeof path === 'string')
        path = path.split('.');
    const orig = obj;
    for (let i = 0; i < path.length - 1; i++) {
        if (!(path[i] in obj)) {
            obj[path[i]] = {};
        }
        obj = obj[path[i]];
    }
    obj[path[path.length - 1]] = value;
    return orig;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function setNode(obj, path, value) {
    setProp(obj, path, value);
}
